import { toast } from 'react-toastify';
import React, { useState, useEffect } from 'react';
import { Pencil, Link as LinkIcon } from 'lucide-react';
import { Unit } from '../types/unit';
import { Video, ModelSpace } from '../types/model';
import { unitService } from '../services/unitService';
import { videoService } from '../services/videoService';
import { Note, getNotes, createNote, deleteNote } from '../services/noteService';
import api from '../services/api';
import SortableList from './SortableList';
import AddItemsModal from './AddItemsModal';
import RichTextEditor from './RichTextEditor';
import ConfirmDialog from './ConfirmDialog';
import PhotoGallery from './PhotoGallery';
import DocumentGallery from './DocumentGallery';
import VideoGallery, { VideoGalleryHandle } from './VideoGallery';
import '../styles/UnitDetailsSlider.css';

interface UnitDetailsSliderProps {
  workspaceId: string;
  unitId: string;
  onClose: () => void;
  onSuccess?: () => void;
}

const UnitDetailsSlider: React.FC<UnitDetailsSliderProps> = ({
  workspaceId,
  unitId,
  onClose,
  onSuccess
}) => {
  const [unit, setUnit] = useState<Unit | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editModeUnitInfo, setEditModeUnitInfo] = useState(false);
  const [editModeFinance, setEditModeFinance] = useState(false);
  const [unitInfoFormData, setUnitInfoFormData] = useState<{
    floorMarketing?: number;
    floorLegal?: string;
    bed?: number;
    bath?: number;
    area?: number;
    unitEnrolmentNo?: string;
  }>({});
  const [financeFormData, setFinanceFormData] = useState<{
    price?: number;
    unitPremium?: number;
    floorPremium?: number;
    tax?: number;
  }>({});

  // Views editing
  const [editingViews, setEditingViews] = useState(false);
  const [viewsData, setViewsData] = useState<string[]>([]);
  const [savingViews, setSavingViews] = useState(false);
  const [showAddViewsModal, setShowAddViewsModal] = useState(false);

  // Spaces editing
  const [editingSpaces, setEditingSpaces] = useState(false);
  const [spacesData, setSpacesData] = useState<string[]>([]);
  const [savingSpaces, setSavingSpaces] = useState(false);
  const [showAddSpacesModal, setShowAddSpacesModal] = useState(false);

  // Notes management
  const [notes, setNotes] = useState<Note[]>([]);
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [showNoteDialog, setShowNoteDialog] = useState(false);
  const [noteContent, setNoteContent] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [deleteNoteId, setDeleteNoteId] = useState<string | null>(null);

  // Video management
  const [videos, setVideos] = useState<Video[]>([]);
  const videoGalleryRef = React.useRef<VideoGalleryHandle>(null);

  // Model views and spaces (fetched from model)
  const [modelViews, setModelViews] = useState<string[]>([]);
  const [modelSpaces, setModelSpaces] = useState<string[]>([]);

  /* data bootstrap effect is placed below after callbacks are declared */

  useEffect(() => {
    // Disable body scroll when modal is open
    document.body.style.overflow = 'hidden';

    return () => {
      // Re-enable body scroll when modal closes
      document.body.style.overflow = '';
    };
  }, []);

  const fetchUnit = React.useCallback(async () => {
    try {
      setLoading(true);
      const [unitResponse, videosData] = await Promise.all([
        unitService.getById(workspaceId, unitId),
        videoService.getVideos(workspaceId, 'unit', unitId)
      ]);
      setUnit(unitResponse.unit);
      setVideos(videosData || []);
      // Initialize form data
      setUnitInfoFormData({
        floorMarketing: unitResponse.unit.floorMarketing,
        floorLegal: unitResponse.unit.floorLegal,
        bed: unitResponse.unit.bed,
        bath: unitResponse.unit.bath,
        area: unitResponse.unit.area,
        unitEnrolmentNo: unitResponse.unit.unitEnrolmentNo
      });
      setFinanceFormData({
        price: unitResponse.unit.price,
        unitPremium: unitResponse.unit.unitPremium,
        floorPremium: unitResponse.unit.floorPremium,
        tax: unitResponse.unit.tax
      });

      // Fetch model views and spaces
      if (unitResponse.unit.modelId) {
        fetchModelViewsAndSpaces(unitResponse.unit.modelId);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch unit details');
      console.error('Error fetching unit:', err);
    } finally {
      setLoading(false);
    }
  }, [workspaceId, unitId]);

  const fetchModelViewsAndSpaces = async (modelId: string) => {
    try {
      const response = await api.get(`/workspaces/${workspaceId}/models/${modelId}`);
      const model = response.data.model;
      setModelViews(model.views || []);
      // Extract space names from ModelSpace objects
      const spaceNames = (model.spaces || []).map((s: ModelSpace | string) =>
        typeof s === 'string' ? s : s.name
      );
      setModelSpaces(spaceNames);
    } catch (err: any) {
      console.error('Error fetching model views/spaces:', err);
    }
  };

  

  const handleUnitInfoEdit = () => {
    if (unit) {
      setUnitInfoFormData({
        floorMarketing: unit.floorMarketing,
        floorLegal: unit.floorLegal,
        bed: unit.bed,
        bath: unit.bath,
        area: unit.area,
        unitEnrolmentNo: unit.unitEnrolmentNo
      });
    }
    setEditModeUnitInfo(true);
  };

  const handleFinanceEdit = () => {
    if (unit) {
      setFinanceFormData({
        price: unit.price,
        unitPremium: unit.unitPremium,
        floorPremium: unit.floorPremium,
        tax: unit.tax
      });
    }
    setEditModeFinance(true);
  };

  const handleUnitInfoSave = async () => {
    try {
      setSaving(true);
      await unitService.update(workspaceId, unitId, unitInfoFormData);
      await fetchUnit();
      setEditModeUnitInfo(false);
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to update unit information');
    } finally {
      setSaving(false);
    }
  };

  const handleFinanceSave = async () => {
    try {
      setSaving(true);
      await unitService.update(workspaceId, unitId, financeFormData);
      await fetchUnit();
      setEditModeFinance(false);
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to update finance information');
    } finally {
      setSaving(false);
    }
  };

  const handleUnitInfoCancel = () => {
    setEditModeUnitInfo(false);
  };

  const handleFinanceCancel = () => {
    setEditModeFinance(false);
  };

  const handleDelete = async () => {
    try {
      await unitService.delete(workspaceId, unitId);
      toast.success('Unit deleted successfully');
      if (onSuccess) {
        onSuccess(); // Trigger parent refresh
      }
      onClose(); // Close slider
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to delete unit');
    }
  };

  const fetchNotes = React.useCallback(async () => {
    try {
      setLoadingNotes(true);
      const fetchedNotes = await getNotes(workspaceId, 'unit', unitId);
      setNotes(fetchedNotes);
    } catch (err: any) {
      console.error('Error fetching notes:', err);
    } finally {
      setLoadingNotes(false);
    }
  }, [workspaceId, unitId]);

  // Bootstrap data when unitId changes
  useEffect(() => {
    fetchUnit();
    fetchNotes();
  }, [fetchUnit, fetchNotes]);

  // Views handlers
  const handleEditViews = () => {
    if (!unit) return;
    setViewsData(unit.views || []);
    setEditingViews(true);
  };

  const handleCancelViews = () => {
    setEditingViews(false);
  };

  const handleViewsChange = (views: string[]) => {
    setViewsData(views);
  };

  const handleAddViewsFromModal = (newViews: string[]) => {
    setViewsData(prev => [...prev, ...newViews]);
  };

  const handleSaveViews = async () => {
    if (!unit) return;

    try {
      setSavingViews(true);
      const response = await unitService.update(workspaceId, unitId, {
        views: viewsData
      });

      setUnit(response.unit);
      setEditingViews(false);
    } catch (err: any) {
      console.error('Error updating views:', err);
      toast.error(err.response?.data?.message || 'Failed to update views');
    } finally {
      setSavingViews(false);
    }
  };

  // Spaces handlers
  const handleEditSpaces = () => {
    if (!unit) return;
    setSpacesData(unit.spaces || []);
    setEditingSpaces(true);
  };

  const handleCancelSpaces = () => {
    setEditingSpaces(false);
  };

  const handleSpacesChange = (spaces: string[]) => {
    setSpacesData(spaces);
  };

  const handleAddSpacesFromModal = (newSpaces: string[]) => {
    setSpacesData(prev => [...prev, ...newSpaces]);
  };

  const handleSaveSpaces = async () => {
    if (!unit) return;

    try {
      setSavingSpaces(true);
      const response = await unitService.update(workspaceId, unitId, {
        spaces: spacesData
      });

      setUnit(response.unit);
      setEditingSpaces(false);
    } catch (err: any) {
      console.error('Error updating spaces:', err);
      toast.error(err.response?.data?.message || 'Failed to update spaces');
    } finally {
      setSavingSpaces(false);
    }
  };

  // Notes handlers
  const handleAddNote = () => {
    setNoteContent('');
    setShowNoteDialog(true);
  };

  const handleSaveNote = async () => {
    if (!noteContent.trim()) {
      toast.warning('Please enter note content');
      return;
    }

    try {
      setSavingNote(true);
      const newNote = await createNote(workspaceId, 'unit', unitId, {
        content: noteContent
      });
      setNotes(prev => [newNote, ...prev]);
      setShowNoteDialog(false);
      setNoteContent('');
    } catch (err: any) {
      console.error('Error saving note:', err);
      toast.error('Failed to save note');
    } finally {
      setSavingNote(false);
    }
  };

  const handleDeleteNote = async (noteId: string) => {
    try {
      await deleteNote(workspaceId, noteId);
      setNotes(prev => prev.filter(n => n._id !== noteId));
      setDeleteNoteId(null);
    } catch (err: any) {
      console.error('Error deleting note:', err);
      toast.error('Failed to delete note');
    }
  };

  // Video handlers
  const handleAddVideo = async (video: Omit<Video, '_id' | 'createdAt'>) => {
    try {
      const newVideo = await videoService.createVideo(workspaceId, 'unit', unitId, {
        title: video.title,
        url: video.url,
        type: video.type,
        thumbnail: video.thumbnail
      });

      setVideos(prev => [...prev, newVideo]);
    } catch (error: any) {
      console.error('Error adding video:', error);
      throw new Error(error.response?.data?.message || 'Failed to add video');
    }
  };

  const handleDeleteVideo = async (videoId: string) => {
    try {
      await videoService.deleteVideo(workspaceId, videoId);
      setVideos(prev => prev.filter(v => v._id !== videoId));
    } catch (error: any) {
      console.error('Error deleting video:', error);
      throw new Error(error.response?.data?.message || 'Failed to delete video');
    }
  };

  const handleUpdateVideo = async (videoId: string, title: string) => {
    try {
      await videoService.updateVideo(workspaceId, videoId, { title });
      setVideos(prev => prev.map(v =>
        v._id === videoId ? { ...v, title } : v
      ));
    } catch (error: any) {
      console.error('Error updating video:', error);
      throw new Error(error.response?.data?.message || 'Failed to update video');
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));

    // If more than 5 days, show full date and time
    if (diffInDays > 5) {
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }

    // If today
    if (diffInDays === 0) {
      if (diffInHours === 0) {
        if (diffInMinutes === 0) {
          return 'Just now';
        }
        return `${diffInMinutes} min${diffInMinutes > 1 ? 's' : ''} ago`;
      }
      return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
    }

    // If within 5 days
    return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
  };

  if (loading) {
    return (
      <div className="model-details-slider unit-details-slider">
        <div className="model-details-loading">
          <div className="builders-loading-spinner" />
        </div>
      </div>
    );
  }

  if (error || !unit) {
    return (
      <div className="model-details-slider unit-details-slider">
        <div className="model-details-error">
          <p>{error || 'Unit not found'}</p>
          <button className="btn btn-secondary btn-md" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="model-details-slider unit-details-slider">
      {/* Header */}
      <div className="model-details-header">
        <div className="model-details-header-left">
          <button className="model-details-back-btn" onClick={onClose}>
            ← Back
          </button>
          <div className="model-details-title">
            <h2>Unit: {unit.unitNo}</h2>
            <p>Project: {unit.projectName} | Model: {unit.modelName}</p>
          </div>
        </div>
        <button className="model-details-close-btn" onClick={onClose}>
          ✕
        </button>
      </div>

      {/* Content */}
      <div className="model-details-content">
        <div className="model-details-grid-wrapper">
          {/* Left Column */}
          <div className="model-details-left">
          {/* Unit Information Tile */}
          <div className="model-details-tile">
            <div className="model-details-tile-header">
              <h3>Unit Information</h3>
              {!editModeUnitInfo && (
                <button
                  className="model-details-tile-edit"
                  onClick={handleUnitInfoEdit}
                  title="Edit unit information"
                  style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#48182F' }}
                >
                  <Pencil size={16} />
                </button>
              )}
            </div>
            {editModeUnitInfo ? (
              <>
                <div className="model-details-tile-content">
                  <div className="model-details-grid">
                    <div className="model-details-field">
                      <label>Unit Number</label>
                      <input
                        type="text"
                        className="form-input"
                        value={unit.unitNo}
                        disabled
                      />
                    </div>
                    <div className="model-details-field">
                      <label>Status</label>
                      <input
                        type="text"
                        className="form-input"
                        value={unit.status}
                        disabled
                      />
                    </div>
                    <div className="model-details-field">
                      <label>Floor (Marketing)</label>
                      <input
                        type="number"
                        className="form-input"
                        value={unitInfoFormData.floorMarketing || ''}
                        onChange={(e) => setUnitInfoFormData({ ...unitInfoFormData, floorMarketing: e.target.value ? parseFloat(e.target.value) : undefined })}
                      />
                    </div>
                    <div className="model-details-field">
                      <label>Floor (Legal)</label>
                      <input
                        type="text"
                        className="form-input"
                        value={unitInfoFormData.floorLegal || ''}
                        onChange={(e) => setUnitInfoFormData({ ...unitInfoFormData, floorLegal: e.target.value })}
                      />
                    </div>
                    <div className="model-details-field">
                      <label>Bedrooms</label>
                      <input
                        type="number"
                        className="form-input"
                        value={unitInfoFormData.bed || ''}
                        onChange={(e) => setUnitInfoFormData({ ...unitInfoFormData, bed: e.target.value ? parseFloat(e.target.value) : undefined })}
                      />
                    </div>
                    <div className="model-details-field">
                      <label>Bathrooms</label>
                      <input
                        type="number"
                        className="form-input"
                        value={unitInfoFormData.bath || ''}
                        onChange={(e) => setUnitInfoFormData({ ...unitInfoFormData, bath: e.target.value ? parseFloat(e.target.value) : undefined })}
                      />
                    </div>
                    <div className="model-details-field">
                      <label>Area (Sq.Ft)</label>
                      <input
                        type="number"
                        className="form-input"
                        value={unitInfoFormData.area || ''}
                        onChange={(e) => setUnitInfoFormData({ ...unitInfoFormData, area: e.target.value ? parseFloat(e.target.value) : undefined })}
                      />
                    </div>
                    <div className="model-details-field">
                      <label>Unit Enrolment Number</label>
                      <input
                        type="text"
                        className="form-input"
                        value={unitInfoFormData.unitEnrolmentNo || ''}
                        onChange={(e) => setUnitInfoFormData({ ...unitInfoFormData, unitEnrolmentNo: e.target.value })}
                      />
                    </div>
                  </div>
                </div>
                <div className="model-details-tile-footer">
                  <button
                    className="btn btn-secondary btn-md"
                    onClick={handleUnitInfoCancel}
                    disabled={saving}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-primary btn-md"
                    onClick={handleUnitInfoSave}
                    disabled={saving}
                  >
                    {saving ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </>
            ) : (
              <div className="model-details-tile-content">
                <div className="model-detail-item">
                  <div className="model-detail-label">Unit Number</div>
                  <div className="model-detail-value">{unit.unitNo}</div>
                </div>
                <div className="model-detail-item">
                  <div className="model-detail-label">Status</div>
                  <div className="model-detail-value">
                    <span className={`status-badge badge-${unit.status.toLowerCase()}`}>
                      {unit.status}
                    </span>
                  </div>
                </div>
                <div className="model-detail-item">
                  <div className="model-detail-label">Floor (Marketing)</div>
                  <div className="model-detail-value">{unit.floorMarketing || '-'}</div>
                </div>
                <div className="model-detail-item">
                  <div className="model-detail-label">Floor (Legal)</div>
                  <div className="model-detail-value">{unit.floorLegal || '-'}</div>
                </div>
                <div className="model-detail-item">
                  <div className="model-detail-label">Bedrooms</div>
                  <div className="model-detail-value">{unit.bed || '-'}</div>
                </div>
                <div className="model-detail-item">
                  <div className="model-detail-label">Bathrooms</div>
                  <div className="model-detail-value">{unit.bath || '-'}</div>
                </div>
                <div className="model-detail-item">
                  <div className="model-detail-label">Area (Sq.Ft)</div>
                  <div className="model-detail-value">{unit.area || '-'}</div>
                </div>
                <div className="model-detail-item">
                  <div className="model-detail-label">Unit Enrolment Number</div>
                  <div className="model-detail-value">{unit.unitEnrolmentNo || '-'}</div>
                </div>
              </div>
            )}
          </div>

          {/* Floor Layout Tile */}
          <div className="model-details-tile">
            <div className="model-details-tile-header">
              <h3>Floor Layout</h3>
            </div>
            <div className="model-details-tile-content">
              <PhotoGallery
                workspaceId={workspaceId}
                entityType="unit"
                entityId={unitId}
                category="floor_layout"
              />
            </div>
          </div>

          {/* Suite Customization Layout Tile */}
          <div className="model-details-tile">
            <div className="model-details-tile-header">
              <h3>Suite Customization Layout</h3>
            </div>
            <div className="model-details-tile-content">
              <PhotoGallery
                workspaceId={workspaceId}
                entityType="unit"
                entityId={unitId}
                category="suite_customization"
              />
            </div>
          </div>

          {/* Electrical Layout Tile */}
          <div className="model-details-tile">
            <div className="model-details-tile-header">
              <h3>Electrical Layout</h3>
            </div>
            <div className="model-details-tile-content">
              <PhotoGallery
                workspaceId={workspaceId}
                entityType="unit"
                entityId={unitId}
                category="electrical_layout"
              />
            </div>
          </div>

          {/* Video Gallery Tile */}
          <div className="model-details-tile">
            <div className="model-details-tile-header">
              <h3>Video Gallery</h3>
              <button
                className="btn btn-primary btn-sm"
                onClick={() => videoGalleryRef.current?.openAddDialog()}
              >
                <LinkIcon size={16} style={{ marginRight: '6px' }} />
                Add Video
              </button>
            </div>
            <div className="model-details-tile-content">
              <VideoGallery
                ref={videoGalleryRef}
                workspaceId={workspaceId}
                entityType="unit"
                entityId={unitId}
                videos={videos}
                onAddVideo={handleAddVideo}
                onDeleteVideo={handleDeleteVideo}
                onUpdateVideo={handleUpdateVideo}
                hideHeader={true}
              />
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div className="model-details-right">
          {/* Finance Information */}
          <div className="model-details-tile">
            <div className="model-details-tile-header">
              <h3>Finance</h3>
              {!editModeFinance && (
                <button
                  className="model-details-tile-edit"
                  onClick={handleFinanceEdit}
                  title="Edit finance information"
                  style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#48182F' }}
                >
                  <Pencil size={16} />
                </button>
              )}
            </div>
            {editModeFinance ? (
              <>
                <div className="model-details-tile-content">
                  <div className="model-details-grid">
                    <div className="model-details-field">
                      <label>Base Price</label>
                      <input
                        type="number"
                        className="form-input"
                        value={financeFormData.price || ''}
                        onChange={(e) => setFinanceFormData({ ...financeFormData, price: e.target.value ? parseFloat(e.target.value) : undefined })}
                      />
                    </div>
                    <div className="model-details-field">
                      <label>Unit Premium</label>
                      <input
                        type="number"
                        className="form-input"
                        value={financeFormData.unitPremium || ''}
                        onChange={(e) => setFinanceFormData({ ...financeFormData, unitPremium: e.target.value ? parseFloat(e.target.value) : undefined })}
                      />
                    </div>
                    <div className="model-details-field">
                      <label>Floor Premium</label>
                      <input
                        type="number"
                        className="form-input"
                        value={financeFormData.floorPremium || ''}
                        onChange={(e) => setFinanceFormData({ ...financeFormData, floorPremium: e.target.value ? parseFloat(e.target.value) : undefined })}
                      />
                    </div>
                    <div className="model-details-field">
                      <label>Tax</label>
                      <input
                        type="number"
                        className="form-input"
                        value={financeFormData.tax || ''}
                        onChange={(e) => setFinanceFormData({ ...financeFormData, tax: e.target.value ? parseFloat(e.target.value) : undefined })}
                      />
                    </div>
                    <div className="model-details-field">
                      <label>Sales Price</label>
                      <input
                        type="text"
                        className="form-input"
                        value={unit.salesPrice ? `$${unit.salesPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-'}
                        disabled
                      />
                    </div>
                    <div className="model-details-field">
                      <label>Price Per Unit (Sq.Ft)</label>
                      <input
                        type="text"
                        className="form-input"
                        value={unit.salesPrice && unit.area ? `$${(unit.salesPrice / unit.area).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-'}
                        disabled
                      />
                    </div>
                  </div>
                </div>
                <div className="model-details-tile-footer">
                  <button
                    className="btn btn-secondary btn-md"
                    onClick={handleFinanceCancel}
                    disabled={saving}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-primary btn-md"
                    onClick={handleFinanceSave}
                    disabled={saving}
                  >
                    {saving ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </>
            ) : (
              <div className="model-details-tile-content">
                <div className="model-detail-item">
                  <div className="model-detail-label">Base Price</div>
                  <div className="model-detail-value">
                    {unit.price ? `$${unit.price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-'}
                  </div>
                </div>
                <div className="model-detail-item">
                  <div className="model-detail-label">Unit Premium</div>
                  <div className="model-detail-value">
                    {unit.unitPremium ? `$${unit.unitPremium.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-'}
                  </div>
                </div>
                <div className="model-detail-item">
                  <div className="model-detail-label">Floor Premium</div>
                  <div className="model-detail-value">
                    {unit.floorPremium ? `$${unit.floorPremium.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-'}
                  </div>
                </div>
                <div className="model-detail-item">
                  <div className="model-detail-label">Tax</div>
                  <div className="model-detail-value">
                    {unit.tax ? `$${unit.tax.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-'}
                  </div>
                </div>
                <div className="model-detail-item">
                  <div className="model-detail-label">Sales Price</div>
                  <div className="model-detail-value">
                    {unit.salesPrice ? `$${unit.salesPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-'}
                  </div>
                </div>
                <div className="model-detail-item">
                  <div className="model-detail-label">Price Per Unit (Sq.Ft)</div>
                  <div className="model-detail-value">
                    {unit.salesPrice && unit.area
                      ? `$${(unit.salesPrice / unit.area).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                      : '-'}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Deal Information */}
          {unit.dealId && (
            <div className="model-details-tile">
              <div className="model-details-tile-header">
                <h3>Deal Information</h3>
              </div>
              <div className="model-details-tile-content">
                <div className="model-detail-item">
                  <div className="model-detail-label">Deal ID</div>
                  <div className="model-detail-value">{unit.dealId}</div>
                </div>
                <div className="model-detail-item">
                  <div className="model-detail-label">Deal Type</div>
                  <div className="model-detail-value">{unit.dealType || '-'}</div>
                </div>
                <div className="model-detail-item">
                  <div className="model-detail-label">Deal Stage</div>
                  <div className="model-detail-value">{unit.dealStage || '-'}</div>
                </div>
              </div>
            </div>
          )}

          {/* Allocations */}
          <div className="model-details-tile">
            <div className="model-details-tile-header">
              <h3>Allocations</h3>
            </div>
            <div className="model-details-tile-content">
              <div className="model-detail-item">
                <div className="model-detail-label">Parking</div>
                <div className="model-detail-value">{unit.allocateParking || 0}</div>
              </div>
              <div className="model-detail-item">
                <div className="model-detail-label">Locker</div>
                <div className="model-detail-value">{unit.allocateLocker || 0}</div>
              </div>
              <div className="model-detail-item">
                <div className="model-detail-label">Bicycle</div>
                <div className="model-detail-value">{unit.allocateBicycle || 0}</div>
              </div>
            </div>
          </div>

          {/* Views and Spaces Row */}
          <div className="model-details-row">
            {/* Views Tile */}
            <div className="model-details-tile model-details-tile-25">
            <div className="model-details-tile-header">
              <h3>Views</h3>
              {!editingViews && (
                <button className="model-details-tile-edit" onClick={handleEditViews} style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#48182F' }}>
                  <Pencil size={16} />
                </button>
              )}
            </div>
            {editingViews ? (
              <>
                <div className="model-details-tile-content">
                  <div className="model-details-sortable-container">
                    <SortableList
                      items={viewsData}
                      onChange={handleViewsChange}
                      placeholder="Add custom view name"
                      addButtonText="+"
                    />
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={() => setShowAddViewsModal(true)}
                      style={{ marginTop: '12px' }}
                    >
                      + Add from List
                    </button>
                  </div>
                </div>
                <div className="model-details-tile-footer">
                  <button
                    className="btn btn-secondary btn-md"
                    onClick={handleCancelViews}
                    disabled={savingViews}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-primary btn-md"
                    onClick={handleSaveViews}
                    disabled={savingViews}
                  >
                    {savingViews ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </>
            ) : (
              <div className="model-details-tile-content">
                {unit.views && unit.views.length > 0 ? (
                  <div className="model-details-list">
                    {unit.views.map((view, index) => (
                      <div key={index} className="model-details-list-item">
                        {view}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="model-details-empty">
                    <p>No views found</p>
                  </div>
                )}
              </div>
            )}
            </div>

            {/* Spaces Tile */}
            <div className="model-details-tile model-details-tile-25">
            <div className="model-details-tile-header">
              <h3>Spaces</h3>
              {!editingSpaces && (
                <button className="model-details-tile-edit" onClick={handleEditSpaces} style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#48182F' }}>
                  <Pencil size={16} />
                </button>
              )}
            </div>
            {editingSpaces ? (
              <>
                <div className="model-details-tile-content">
                  <div className="model-details-sortable-container">
                    <SortableList
                      items={spacesData}
                      onChange={handleSpacesChange}
                      placeholder="Add custom space name"
                      addButtonText="+"
                    />
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={() => setShowAddSpacesModal(true)}
                      style={{ marginTop: '12px' }}
                    >
                      + Add from List
                    </button>
                  </div>
                </div>
                <div className="model-details-tile-footer">
                  <button
                    className="btn btn-secondary btn-md"
                    onClick={handleCancelSpaces}
                    disabled={savingSpaces}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-primary btn-md"
                    onClick={handleSaveSpaces}
                    disabled={savingSpaces}
                  >
                    {savingSpaces ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </>
            ) : (
              <div className="model-details-tile-content">
                {unit.spaces && unit.spaces.length > 0 ? (
                  <div className="model-details-list">
                    {unit.spaces.map((space, index) => (
                      <div key={index} className="model-details-list-item">
                        {space}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="model-details-empty">
                    <p>No spaces found</p>
                  </div>
                )}
              </div>
            )}
            </div>
          </div>

          {/* Key Maps Tile */}
          <div className="model-details-tile">
            <div className="model-details-tile-header">
              <h3>Key Maps</h3>
            </div>
            <div className="model-details-tile-content">
              <PhotoGallery
                workspaceId={workspaceId}
                entityType="unit"
                entityId={unitId}
                category="key_maps"
              />
            </div>
          </div>

          {/* Notes Tile */}
          <div className="model-details-tile">
            <div className="model-details-tile-header">
              <h3>Notes</h3>
              <button className="btn btn-primary btn-sm" onClick={handleAddNote}>Add</button>
            </div>
            <div className="model-details-tile-content">
              {loadingNotes ? (
                <div className="model-details-loading">Loading notes...</div>
              ) : notes.length > 0 ? (
                <div className="model-details-notes-list">
                  {notes.map(note => (
                    <div key={note._id} className="model-details-note-item">
                      <div className="model-details-note-header">
                        <div className="model-details-note-meta">
                          <span className="model-details-note-author">
                            By {note.createdByName || note.createdBy}
                          </span>
                          <span className="model-details-note-separator">•</span>
                          <span className="model-details-note-date">
                            {formatDate(note.createdAt)}
                          </span>
                        </div>
                        <button
                          className="model-details-note-delete"
                          onClick={() => setDeleteNoteId(note._id)}
                          title="Delete note"
                        >
                          ✕
                        </button>
                      </div>
                      <div
                        className="model-details-note-content"
                        dangerouslySetInnerHTML={{ __html: note.content }}
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="model-details-empty">
                  <p>No notes found</p>
                </div>
              )}
            </div>
          </div>

          {/* Documents Tile */}
          <div className="model-details-tile">
            <div className="model-details-tile-header">
              <h3>Documents</h3>
            </div>
            <div className="model-details-tile-content">
              <DocumentGallery
                workspaceId={workspaceId}
                entityType="unit"
                entityId={unitId}
                fileType="document"
                acceptTypes=".pdf,.doc,.docx"
                helpText="All unit related documents can be uploaded here. PDF and Word files are accepted."
              />
            </div>
          </div>

          {/* Photo Gallery Tile */}
          <div className="model-details-tile">
            <div className="model-details-tile-header">
              <h3>Photo Gallery</h3>
            </div>
            <div className="model-details-tile-content">
              <PhotoGallery
                workspaceId={workspaceId}
                entityType="unit"
                entityId={unitId}
                category="photo_gallery"
              />
            </div>
          </div>
        </div>
        </div>
      </div>

      {/* Footer */}
      <div className="model-details-footer">
        <button
          className="btn btn-danger btn-sm"
          onClick={() => setShowDeleteModal(true)}
        >
          Delete Unit
        </button>
        <button
          className="btn btn-secondary btn-sm"
          onClick={onClose}
        >
          Close
        </button>
      </div>

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="model-details-modal-overlay">
          <div className="model-details-modal">
            <h3>Delete Unit</h3>
            <p>
              Are you sure you want to delete unit <strong>{unit.unitNo}</strong>? This action cannot be undone.
            </p>
            <div className="model-details-modal-actions">
              <button
                className="btn btn-secondary btn-md"
                onClick={() => setShowDeleteModal(false)}
              >
                Cancel
              </button>
              <button
                className="btn btn-danger btn-md"
                onClick={handleDelete}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Views Modal */}
      <AddItemsModal
        isOpen={showAddViewsModal}
        onClose={() => setShowAddViewsModal(false)}
        onAdd={handleAddViewsFromModal}
        availableOptions={modelViews.filter(option => !viewsData.includes(option))}
        title="Add Views"
        customInputPlaceholder="Enter custom view name..."
        emptyMessage={modelViews.length > 0 ? "All views from this model are already added." : "No views configured for this model."}
      />

      {/* Add Spaces Modal */}
      <AddItemsModal
        isOpen={showAddSpacesModal}
        onClose={() => setShowAddSpacesModal(false)}
        onAdd={handleAddSpacesFromModal}
        availableOptions={modelSpaces.filter(option => !spacesData.includes(option))}
        title="Add Spaces"
        customInputPlaceholder="Enter custom space name..."
        emptyMessage={modelSpaces.length > 0 ? "All spaces from this model are already added." : "No spaces configured for this model."}
      />

      {/* Add Note Dialog */}
      {showNoteDialog && (
        <div className="model-details-modal-overlay" onClick={() => setShowNoteDialog(false)}>
          <div className="model-details-modal model-details-note-dialog" onClick={(e) => e.stopPropagation()}>
            <h3>Add Note</h3>
            <RichTextEditor
              content={noteContent}
              onChange={setNoteContent}
              placeholder="Enter note content..."
            />
            <div className="model-details-modal-actions">
              <button
                className="btn btn-secondary btn-md"
                onClick={() => setShowNoteDialog(false)}
                disabled={savingNote}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary btn-md"
                onClick={handleSaveNote}
                disabled={savingNote}
              >
                {savingNote ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Note Confirmation */}
      <ConfirmDialog
        isOpen={deleteNoteId !== null}
        title="Delete Note"
        message="Are you sure you want to delete this note? This action cannot be undone."
        onConfirm={() => deleteNoteId && handleDeleteNote(deleteNoteId)}
        onClose={() => setDeleteNoteId(null)}
      />
    </div>
  );
};

export default UnitDetailsSlider;